import { Logger } from '@footsteps/shared';
import { WozCaseEntity } from '../../woz-case/entities';
import type { WozCaseStatus } from '../../woz-case/woz-case.types';
import type { WozCaseDetailResponse } from '../supabase-api/supabase-api.types';
import type { WozCaseStorageAdapter } from './woz-case-storage.adapter';
import { SupabaseApiService } from '../supabase-api/supabase-api.service';

/**
 * Database adapter for WOZ cases.
 * Uses Supabase Edge Functions for all database operations (no PostgREST).
 * RLS policies scoped to auth.uid() are enforced server-side.
 */
export class WozCaseStorageDatabaseAdapter implements WozCaseStorageAdapter {
  private readonly logger: Logger;
  private readonly api: SupabaseApiService;

  constructor() {
    this.logger = new Logger(this.constructor.name);
    this.api = SupabaseApiService.getInstance();
  }

  async save(entity: WozCaseEntity): Promise<WozCaseEntity> {
    const payload = this.toPayload(entity);

    if (entity.id) {
      this.logger.debug(`update case: ${entity.id}`);
      const result = await this.api.updateCase(entity.id, payload);
      entity.id = result.id;
      entity.source = 'database';
      this.syncComplaintIds(entity, result.complaints);
    } else {
      this.logger.debug('create new case');
      const result = await this.api.createCase(payload);
      entity.id = result.id;
      entity.source = 'database';
      this.syncComplaintIds(entity, result.complaints);
    }

    return entity;
  }

  async load(caseId?: string): Promise<WozCaseEntity | null> {
    if (!caseId) return null;

    this.logger.debug(`restore case: ${caseId}`);
    try {
      const data = await this.api.restoreCase(caseId);
      return new WozCaseEntity(data);
    } catch (error) {
      this.logger.error('Failed to restore case', error);
      return null;
    }
  }

  async fetchOpen(): Promise<WozCaseEntity[]> {
    this.logger.debug('fetchOpen');
    try {
      const items = await this.api.fetchCases();
      return items
        .filter((item) => item.status === 'open')
        .map(
          (item) =>
            new WozCaseEntity({
              ...item,
              source: 'database',
              status: item.status as WozCaseStatus,
              complaintMode: item.complaintMode as 'single' | 'multi' | null,
              complaints: [],
              messages: [],
              flow: null,
            })
        );
    } catch (error) {
      this.logger.error('Failed to fetch open cases', error);
      return [];
    }
  }

  async remove(caseId?: string): Promise<void> {
    if (!caseId) return;
    this.logger.debug(`remove: ${caseId} (not yet implemented)`);
  }

  async clear(): Promise<void> {
    this.logger.debug('clear (not yet implemented)');
  }

  /**
   * After a create/update round-trip, assign the DB-generated complaint
   * (and question) IDs back onto the entity by matching on sequence.
   */
  private syncComplaintIds(entity: WozCaseEntity, dbComplaints: WozCaseDetailResponse['complaints']): void {
    for (const dbComplaint of dbComplaints) {
      const local = entity.complaints.find((c) => c.sequence === dbComplaint.sequence);
      if (local) {
        local.id = dbComplaint.id;
      }
    }
  }

  /**
   * Convert a WozCaseEntity into the payload shape expected by the
   * edge function create/update endpoints.
   */
  private toPayload(entity: WozCaseEntity): Record<string, unknown> {
    const data = entity.toStorage();
    return {
      addressPointId: data.addressPointId ?? '',
      addressCity: data.addressCity ?? '',
      addressHouseNumber: Number(data.addressHouseNumber ?? 0),
      addressHouseLetter: data.addressHouseLetter ?? null,
      addressHouseNumberAddition: data.addressHouseNumberAddition ?? null,
      addressPostalCode: data.addressPostalCode ?? '',
      addressStreet: data.addressStreet ?? '',
      complaintMode: data.complaintMode ?? 'single',
      status: data.status,
      flow: data.flow,
      messages: data.messages,
      signatureData: data.signatureData ?? null,
      dateSigned: data.dateSigned ?? null,
      classificationRationale: data.classificationRationale ?? null,
      originalComplaint: data.originalComplaint ?? null,
      originalComplaintFormatted: data.originalComplaintFormatted ?? null,
      complaints: data.complaints.map((complaint, index) => ({
        sequence: complaint.sequence ?? index,
        categoryCode: complaint.categoryCode,
        correctedText: complaint.correctedText,
        formattedText: complaint.formattedText,
        hash: complaint.hash,
        text: complaint.text,
        classificationRationale: complaint.classificationRationale ?? null,
        detectedReferences: complaint.detectedReferences ?? [],
        questions: complaint.questions.map((question) => {
          if (question.type === 'action') {
            let json: string | null = null;
            if (question.answer != null) {
              json = typeof question.answer === 'string' ? question.answer : JSON.stringify(question.answer);
            }
            return { questionId: question.questionId || question.id || '', answer: json, selectedOptionIds: [] };
          }

          const selectedOptionIds: string[] = [];
          if (Array.isArray(question.answer)) {
            selectedOptionIds.push(...(question.answer as string[]));
          } else if (question.answer && question.type !== 'open') {
            selectedOptionIds.push(question.answer as string);
          }

          return {
            questionId: question.questionId || question.id || '',
            answer: question.type === 'open' && typeof question.answer === 'string' ? question.answer : null,
            selectedOptionIds,
          };
        }),
        references: (complaint.references ?? []).map((ref) => ({
          type: ref.type,
          startPosition: ref.startPosition,
          endPosition: ref.endPosition,
          citation: ref.citation,
          value: ref.value,
          metadata: ref.metadata ?? null,
        })),
      })),
    };
  }
}
